import { useCallback, useEffect, useRef, useState, type CSSProperties } from "react";
import { createPortal } from "react-dom";
import { parseVisitorNameAnswer } from "@shared/receptionistName";
import { MessageSquare, Send, X, Loader2, UserRound, ArrowRight } from "lucide-react";
import { useSiteConfig } from "../SiteConfigContext";
import {
  isOwnerWebsiteHref,
  publicWebsiteTargetHref,
  requestVaultNavigate,
  scrollToClassSchedule,
  WEBSITE_RECEPTIONIST_OPEN_EVENT,
} from "../internalNav";
import { trackEvent } from "@/lib/analytics";
import { redirectAfterSubmission } from "@/lib/submissionConfirmation";
import { launcherImageFramingStyle } from "@/components/receptionistLauncherFraming";
import AppointmentBooking, { savedConfirmation } from "./AppointmentBooking";
import {
  clearReceptionistBookingContext,
  getReceptionistBookingContext,
  saveReceptionistBookingContext,
  type SignedReceptionistContext,
} from "./receptionistBooking";

// ── Digital Receptionist widget (Task 1491) ────────────────────────────────
// Floating circular launcher (bottom-right) that opens the KB-grounded chat
// panel on the public page. Answers come from POST /api/ic/receptionist/chat
// (owner resolved by slug); when the model surfaces lead details, the widget
// posts them through the standard POST /api/ic/submit path (formSource
// "receptionist") so the owner's Contact lead-capture settings (notifications,
// follow-up task + email) all apply.
//
// Portaled to <body> so the fixed launcher/panel can never be trapped by a
// scroll-reveal `transform` ancestor (see vault-lightbox-portal gotcha). Since
// the portal escapes .vault-template-root, the scoped --theme-* vars are
// re-read from the root and re-applied on the portal wrapper.
//
// Explicit failure contract: a 503 (no OpenAI key) or 502 shows a clear
// "can't chat right now" line in the thread — there is NO canned-answer
// fallback. Preview route (no slug) never POSTs; it shows a preview notice.

// Other page surfaces (the footer "Contact Us" link, the legal-page
// #contact-us deep link, the hero "Get Our Pricing" button) open the
// receptionist by dispatching this event. `flow: "pricing"` starts the guided
// pricing conversation (name → contact → grounded pricing answer); `waitlist`
// starts with the named offering rather than the generic greeting.
export const OPEN_RECEPTIONIST_EVENT = WEBSITE_RECEPTIONIST_OPEN_EVENT;
export type OpenReceptionistDetail = {
  flow?: "pricing" | "general" | "waitlist";
  offering?: string;
  sessionPurpose?: "regular" | "consultation";
};
export function openReceptionist(detail?: OpenReceptionistDetail) {
  window.dispatchEvent(
    new CustomEvent<OpenReceptionistDetail>(OPEN_RECEPTIONIST_EVENT, { detail: detail ?? {} }),
  );
}

type ChatMessage = { role: "user" | "assistant"; content: string };
// System rows render as muted status lines inside the thread (errors, the
// "details passed along" confirmation) — they are never sent to the model.
// `local` marks assistant-styled rows the CLIENT fabricated (the greeting,
// the preview notice) — they render like assistant bubbles but are never sent
// to the server, because the server now verifies assistant history against
// the HMAC historyToken it issued and would reject turns it never produced.
type ThreadRow = (ChatMessage & { local?: boolean; greeting?: boolean }) | { role: "system"; content: string };

const MAX_MESSAGE_CHARS = 2000;
const receptionistIntro = (name: string) =>
  `Hi, I'm ${name}. I can help answer most questions you might have or I can pass a message along to the team.\n\nHow can I best help you?`;
const LEGACY_DIGI_INTRO = receptionistIntro("Digi");
const LAUNCHER_COLLAPSED_KEY = "ic-receptionist-launcher-collapsed";

// ── Guided pricing flow (task 1613) ───────────────────────────────────────
// The hero "Get Our Pricing" button opens the widget in a short guided
// conversation: name → contact details (email always;
// phone only when the owner's contact form asks for it) → a grounded pricing
// answer from the server chat. The guided question/answer rows are CLIENT
// copy (never sent to the model); once the details are in, the widget posts
// the lead through /api/ic/submit and then asks the server ONE composed
// pricing question — only the reply is displayed.
type GuidedStage = "name" | "lastName" | "email" | "phone";
type GuidedAnswers = { name: string; firstName: string; lastName: string; email: string; phone: string; context: string };

const GUIDED_PROMPTS: Record<GuidedStage, string> = {
  name: "I'd be happy to share our pricing. What's your name?",
  lastName: "And your last name?",
  email: "Great to meet you! What's the best email to reach you at?",
  phone: "And what's a good phone number for you?",
};

// Render URLs inside chat text as clickable links. External destinations open
// in a new tab; owner-site destinations stay in the current tab on legal pages.
// Include exact appointment-slide links, including line breaks inserted after
// "?" by older chat replies. Never interpret arbitrary relative text as a URL.
const URL_RE = /(https?:\/\/[^\s<>()]+|www\.[^\s<>()]+|\?\s*slide=\d+#services|\?tab=sessions#class-schedule)/gi;
const NATIVE_SESSIONS_PATH = "?tab=sessions#class-schedule";
function renderWithLinks(text: string, onNativeSessionsClick?: () => void, legalHomeHref?: string, onNativeSlideClick?: (id: string) => void) {
  const parts = text.split(URL_RE);
  if (parts.length === 1) return text;
  return parts.map((part, i) => {
    if (!part.match(URL_RE)) return part;
    // Peel trailing punctuation off the link.
    const trimmed = part.replace(/[.,;:!?)\]]+$/, "");
    const rest = part.slice(trimmed.length);
    const href = trimmed.startsWith("www.") ? `https://${trimmed}` : trimmed;
    const isNativeSessionsLink = trimmed === NATIVE_SESSIONS_PATH;
    const slideId = trimmed.match(/^\?\s*slide=(\d+)#services$/i)?.[1];
    const isNativeLink = isNativeSessionsLink || Boolean(slideId);
    const linkHref = slideId
      ? `${legalHomeHref ? publicWebsiteTargetHref(legalHomeHref, { kind: "slide", id: slideId }) : `?slide=${slideId}`}#services`
      : isNativeSessionsLink && legalHomeHref
        ? publicWebsiteTargetHref(legalHomeHref, { kind: "schedule" })
        : href;
    const isOwnerWebsiteLink = Boolean(legalHomeHref && isOwnerWebsiteHref(href, legalHomeHref));
    return (
      <span key={i}>
        <a
          href={linkHref}
          target={isNativeLink || isOwnerWebsiteLink ? undefined : "_blank"}
          rel={isNativeLink || isOwnerWebsiteLink ? undefined : "noopener noreferrer"}
          onClick={isNativeLink && !legalHomeHref && (slideId ? onNativeSlideClick : onNativeSessionsClick)
            ? (event) => {
                event.preventDefault();
                if (slideId) onNativeSlideClick?.(slideId);
                else onNativeSessionsClick?.();
              }
            : undefined}
          className={`underline underline-offset-2 ${isNativeLink ? "break-normal" : "break-all"}`}
          style={{ color: "var(--theme-accent)" }}
        >
          {isNativeLink ? "View booking availability" : trimmed}
        </a>
        {rest}
      </span>
    );
  });
}

// postMessage contract for the legal-page iframe embed: when `embedded` is
// true (the widget is the ONLY content of a small same-origin iframe on the
// server-rendered /p/<slug>/terms|privacy pages), the widget reports its
// open/closed state to the parent so the parent can resize the iframe —
// launcher-sized while closed, panel-sized while open. Same-origin only.
export const RECEPTIONIST_EMBED_MESSAGE = "ic-receptionist-embed";

type ReceptionistWidgetProps = {
  slug?: string;
  embedded?: boolean;
  customDomain?: boolean;
  legalHomeHref?: string;
};

export default function ReceptionistWidget(props: ReceptionistWidgetProps) {
  const { content } = useSiteConfig();
  if (content.receptionist.enabled === false) return null;
  return <EnabledReceptionistWidget {...props} />;
}

function EnabledReceptionistWidget({
  slug,
  embedded = false,
  customDomain = false,
  legalHomeHref,
}: ReceptionistWidgetProps) {
  // SSR + hydration guard: this widget renders through
  // createPortal(document.body), which doesn't exist during the server render
  // of /p/<slug> (and the root sales page). Render nothing until after mount —
  // null on the server AND on the client's first hydration pass (so the two
  // match — a typeof-document branch alone trips React's hydration mismatch),
  // then the portal mounts in the post-hydration effect pass.
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);
  const { content } = useSiteConfig();
  const name = content.receptionist.name.trim();
  // Blank name → "Digi" (July 2026, user request). The server payload already
  // defaults blank to "Digi"; this client fallback covers preview/design routes.
  const title = name || "Digi";
  const launcherText = content.receptionist.launcherText.trim() || "Instant Answers";
  const launcherSubtext =
    content.receptionist.launcherSubtext.trim() || "from Our Digital Receptionist";
  const launcherImageUrl = content.receptionist.launcherImageUrl?.trim() || "";
  const launcherImageZoom = content.receptionist.launcherImageZoom ?? 100;
  const launcherImageFocalX = content.receptionist.launcherImageFocalX ?? 50;
  const launcherImageFocalY = content.receptionist.launcherImageFocalY ?? 50;
  const [launcherImageFailed, setLauncherImageFailed] = useState(false);
  useEffect(() => setLauncherImageFailed(false), [launcherImageUrl]);
  const isPreview = !slug;

  const [open, setOpen] = useState(false);
  const [launcherExpanded, setLauncherExpanded] = useState(true);
  const [thread, setThread] = useState<ThreadRow[]>([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [bookingAvailable, setBookingAvailable] = useState(false);
  const [bookingOpen, setBookingOpen] = useState(false);
  const [bookingContact, setBookingContact] = useState<{ name: string; email: string; phone: string }>({ name: "", email: "", phone: "" });
  // One lead submission per conversation — the model can keep emitting `lead`
  // on later turns; only the first actionable one is posted.
  const leadSubmittedRef = useRef(false);
  const navigateToSessions = useCallback(() => {
    setOpen(false);
    scrollToClassSchedule();
  }, []);
  const navigateToSlide = useCallback((id: string) => {
    setOpen(false);
    requestVaultNavigate({ kind: "slide", id });
  }, []);
  // Conversation integrity token from the last server reply (see server
  // comment on /api/ic/receptionist/chat). Sent with each subsequent turn so
  // the server can verify the assistant history wasn't tampered with.
  const historyTokenRef = useRef<string | null>(null);
  const conversationIdRef = useRef<string | null>(null);
  const waitlistContextRef = useRef<{
    offering: string;
    sessionPurpose: "regular" | "consultation";
  } | null>(null);
  // Server-facing transcript (source of truth for what the server signed with
  // historyToken). Kept separate from the display `thread` so a mid-flight
  // recovery (token rejected after a server restart) can reset the server
  // conversation without wiping what the visitor sees.
  const transcriptRef = useRef<ChatMessage[]>([]);
  const rememberBookingContext = useCallback((data: any, transcript: ChatMessage[]) => {
    const token = typeof data?.historyToken === "string" ? data.historyToken : null;
    const id = typeof data?.conversationId === "string" ? data.conversationId : null;
    historyTokenRef.current = token;
    conversationIdRef.current = id;
    transcriptRef.current = transcript;
    if (slug && token) {
      saveReceptionistBookingContext({ slug, conversationId: id, historyToken: token, transcript });
    }
    const lead = data?.lead;
    const userLines = transcript.filter((message) => message.role === "user").map((message) => message.content);
    const inferredEmail = [...userLines].reverse().map((line) => line.match(/[^\s@]+@[^\s@]+\.[^\s@]+/)?.[0]).find(Boolean);
    const inferredPhone = [...userLines].reverse().map((line) => line.match(/\+?[\d(). -]{7,}/)?.[0]).find(Boolean);
    const inferredName = [...userLines].slice(-6).reverse()
      .map((line) => parseVisitorNameAnswer(line))
      .find((name) => name?.fullName)?.fullName;
    if (lead && typeof lead === "object") {
      const first = typeof lead.firstName === "string" ? lead.firstName : "";
      const last = typeof lead.lastName === "string" ? lead.lastName : "";
      setBookingContact((current) => ({
        name: [first, last].filter(Boolean).join(" ") || (typeof lead.name === "string" ? lead.name : inferredName || current.name),
        email: typeof lead.email === "string" ? lead.email : inferredEmail || current.email,
        phone: typeof lead.phone === "string" ? lead.phone : inferredPhone || current.phone,
      }));
    } else {
      setBookingContact((current) => ({
        ...current,
        name: inferredName || current.name,
        email: inferredEmail || current.email,
        phone: inferredPhone || current.phone,
      }));
    }
    if (data?.bookingRequested === true) setBookingOpen(true);
    // The chat model's flags never fabricate availability. Booking controls
    // are revealed only after the public availability endpoint confirms slots.
    if (slug) {
      void fetch(`/api/ic/receptionist/booking/${encodeURIComponent(slug)}`)
        .then((response) => response.ok ? response.json() : null)
        .then((availability) => setBookingAvailable(
          Array.isArray(availability?.offerings) &&
          availability.offerings.some((item: { slots?: unknown[] }) => Array.isArray(item.slots) && item.slots.length > 0),
        ))
        .catch(() => setBookingAvailable(false));
    }
  }, [slug]);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLTextAreaElement | null>(null);
  const firstMessageTrackedRef = useRef(false);
  useEffect(() => {
    try {
      setLauncherExpanded(window.sessionStorage.getItem(LAUNCHER_COLLAPSED_KEY) !== "1");
    } catch {
      // Blocked session storage keeps the benefit-led launcher visible.
    }
  }, []);

  const collapseLauncher = useCallback(() => {
    setLauncherExpanded(false);
    try {
      window.sessionStorage.setItem(LAUNCHER_COLLAPSED_KEY, "1");
    } catch {
      // Best-effort progressive disclosure only.
    }
  }, []);

  const openFromLauncher = useCallback(() => {
    collapseLauncher();
    trackEvent("receptionist_launcher_open", { source: "floating_launcher" });
    setOpen(true);
  }, [collapseLauncher]);
  useEffect(() => {
    if (open) collapseLauncher();
  }, [open, collapseLauncher]);

  // Guided pricing flow state (see GUIDED_PROMPTS above). `guidedStage` is
  // the question currently awaiting an answer; null = normal free chat.
  const [guidedStage, setGuidedStage] = useState<GuidedStage | null>(null);
  const guidedAnswersRef = useRef<GuidedAnswers>({ name: "", firstName: "", lastName: "", email: "", phone: "", context: "" });
  const [pendingConsent, setPendingConsent] = useState<
    | { kind: "guided" }
    | {
        kind: "general";
        lead: { firstName?: string; lastName?: string; email?: string; phone?: string; summary?: string };
        transcript: ChatMessage[];
      }
    | null
  >(null);
  const [phoneConsentOffered, setPhoneConsentOffered] = useState(false);
  // Older cached public payloads and embedders may not have the isolated
  // receptionist form block yet. Keep their existing behavior until refreshed.
  const askPhone = content.receptionist.contactForm?.phone ?? content.contactForm.phone;
  const requireLastName = content.receptionist.contactForm?.lastNameRequired ?? content.contactForm.lastNameRequired;

  // ── Conversation persistence ──────────────────────────────────────────────
  // Remember the conversation (visible thread + signed transcript + tokens +
  // lead-submitted flag) in localStorage for 24h, keyed per site slug, so a
  // page refresh, navigation, or a return visit later the same day picks the
  // chat back up — the model keeps the visitor's name/email/context instead
  // of asking again. If the persisted historyToken has gone stale (server
  // redeploy), the existing history_token_invalid recovery path handles it.
  const storageKey = slug ? `ic-receptionist:${slug}` : null;
  const restoredRef = useRef(false);
  useEffect(() => {
    if (!storageKey || restoredRef.current) return;
    restoredRef.current = true;
    try {
      const raw = window.localStorage.getItem(storageKey);
      if (!raw) return;
      const saved = JSON.parse(raw);
      if (!saved || typeof saved !== "object") return;
      if (typeof saved.savedAt !== "number" || Date.now() - saved.savedAt > 24 * 60 * 60 * 1000) {
        window.localStorage.removeItem(storageKey);
        return;
      }
      if (Array.isArray(saved.thread) && saved.thread.length > 0) setThread(saved.thread);
      if (Array.isArray(saved.transcript)) transcriptRef.current = saved.transcript;
      historyTokenRef.current = typeof saved.historyToken === "string" ? saved.historyToken : null;
      conversationIdRef.current = typeof saved.conversationId === "string" ? saved.conversationId : null;
      if (slug && historyTokenRef.current && Array.isArray(saved.transcript)) {
        saveReceptionistBookingContext({
          slug,
          conversationId: conversationIdRef.current,
          historyToken: historyTokenRef.current,
          transcript: saved.transcript,
        });
      }
      leadSubmittedRef.current = saved.leadSubmitted === true;
      if (saved.phoneConsentOffered === true) setPhoneConsentOffered(true);
      if (
        saved.pendingConsent?.kind === "general" &&
        saved.pendingConsent.lead &&
        typeof saved.pendingConsent.lead.phone === "string" &&
        Array.isArray(saved.pendingConsent.transcript)
      ) {
        setPendingConsent({
          kind: "general",
          lead: saved.pendingConsent.lead,
          transcript: saved.pendingConsent.transcript,
        });
      }
    } catch {
      // Corrupt/blocked storage — start fresh.
    }
  }, [storageKey]);
  useEffect(() => {
    if (!storageKey) return;
    // Nothing worth saving until a real exchange happened (greeting is local).
    if (transcriptRef.current.length === 0) return;
    try {
      window.localStorage.setItem(
        storageKey,
        JSON.stringify({
          savedAt: Date.now(),
          thread,
          transcript: transcriptRef.current,
          historyToken: historyTokenRef.current,
          conversationId: conversationIdRef.current,
          leadSubmitted: leadSubmittedRef.current,
          phoneConsentOffered,
          pendingConsent: pendingConsent?.kind === "general" ? pendingConsent : null,
        }),
      );
    } catch {
      // Storage full/blocked — persistence is best-effort only.
    }
  }, [storageKey, thread, phoneConsentOffered, pendingConsent]);

  // Re-apply the root-scoped theme vars on the portaled wrapper (the portal
  // escapes .vault-template-root, so the vars don't inherit). Re-snapshot
  // whenever the site config content changes (the page applies the owner's
  // accent to the root's inline style only once the config resolves — a
  // one-shot mount snapshot races that and freezes the CSS default accent on
  // the portal, painting the launcher the wrong color on any page whose
  // accent differs from the template default) and again on open for safety.
  const [themeVars, setThemeVars] = useState<CSSProperties>({});
  useEffect(() => {
    const root = document.querySelector('[data-testid="vault-template-root"]');
    if (!root) return;
    const cs = getComputedStyle(root as HTMLElement);
    const pick = (n: string, fb: string) => cs.getPropertyValue(n).trim() || fb;
    setThemeVars({
      // The light panel uses the light-section accent. Its dark launcher and
      // accent outline need the owner's dark-section accent and CTA colors too.
      ["--theme-accent" as string]: pick("--theme-accent", "#F4AB15"),
      ["--theme-accent-dark" as string]: pick("--theme-accent-dark", "#F4AB15"),
      ["--theme-cta-bg" as string]: pick("--theme-cta-bg", "#1F1F1F"),
      ["--theme-cta-fg" as string]: pick("--theme-cta-fg", "#FFFFFF"),
    } as CSSProperties);
  }, [content, open]);

  // Embedded (legal-page iframe): tell the parent page whether the panel is
  // open so it can resize the iframe. Sent to the same origin only.
  useEffect(() => {
    if (!embedded || !mounted) return;
    try {
      window.parent?.postMessage(
        { type: RECEPTIONIST_EMBED_MESSAGE, open },
        window.location.origin,
      );
    } catch {
      // Best-effort — a blocked postMessage just leaves the iframe small.
    }
  }, [embedded, mounted, open]);

  // Embedded: the parent legal page's footer "Contact Us" link opens the chat
  // by postMessaging into this iframe (same origin only).
  useEffect(() => {
    if (!embedded) return;
    const onMessage = (e: MessageEvent) => {
      if (e.origin !== window.location.origin) return;
      const d = e.data as { type?: string } | null;
      if (d && d.type === "ic-receptionist-open") setOpen(true);
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [embedded]);

  // Footer "Contact Us" + legal-page #contact-us deep link open the widget.
  // The hero "Get Our Pricing" button passes `flow: "pricing"` to start the
  // guided pricing conversation: the widget asks its first question as a
  // local assistant row and waits for the visitor's answer in send().
  useEffect(() => {
    // Start (or resume) the guided pricing flow. `opener` swaps the first
    // local assistant line — the Window QR entry leads with pricing/intro
    // offers instead of the hero button's generic pricing intro.
    const startPricingFlow = (opener?: string) => {
      setOpen(true);
      setGuidedStage((stage) => {
        if (stage) return stage; // already mid-flow — don't restart
        setThread((t) => [
          ...t,
          { role: "assistant", local: true, content: opener || GUIDED_PROMPTS.name },
        ]);
        return "name";
      });
    };
    const openWidget = (e: Event) => {
      setOpen(true);
      const detail = (e as CustomEvent<OpenReceptionistDetail>).detail;
      if (detail?.flow === "pricing") startPricingFlow();
      if (detail?.flow === "waitlist") {
        const offering = detail.offering?.trim() || (detail.sessionPurpose === "consultation" ? "Consultation" : "Training Session");
        waitlistContextRef.current = {
          offering,
          sessionPurpose: detail.sessionPurpose === "consultation" ? "consultation" : "regular",
        };
        setThread((t) => [
          ...t,
          {
            role: "assistant",
            local: true,
            content: `I can help you join the waitlist for ${offering}. Tell me your name, email, and any scheduling preferences.`,
          },
        ]);
      }
    };
    window.addEventListener(OPEN_RECEPTIONIST_EVENT, openWidget);
    const openFromHash = () => {
      if (window.location.hash === "#contact-us") setOpen(true);
    };
    openFromHash();
    window.addEventListener("hashchange", openFromHash);
    // Trackable Asset destinations can open pricing, a general conversation,
    // or the consultation scheduling conversation directly.
    if (!embedded && slug) {
      const digi = new URLSearchParams(window.location.search).get("digi");
      if (digi === "pricing") {
        startPricingFlow(
          "Want to see our pricing and intro offers? I can help with that. What's your name?",
        );
      } else if (digi === "general") {
        setOpen(true);
      } else if (digi === "consult") {
        setOpen(true);
        waitlistContextRef.current = {
          offering: "Consultation",
          sessionPurpose: "consultation",
        };
        setThread([
          {
            role: "assistant",
            local: true,
            content: "I can help you schedule a consultation. Tell me your name, email, and any scheduling preferences.",
          },
        ]);
      }
      if (digi === "pricing" || digi === "general" || digi === "consult") {
        const url = new URL(window.location.href);
        url.searchParams.delete("digi");
        window.history.replaceState(null, "", url.pathname + url.search + url.hash);
      }
    }
    return () => {
      window.removeEventListener(OPEN_RECEPTIONIST_EVENT, openWidget);
      window.removeEventListener("hashchange", openFromHash);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Greeting on first open (client-side copy only — no model call).
  useEffect(() => {
    if (!open || thread.length > 0) return;
    setThread([
      {
        role: "assistant",
        local: true,
        greeting: true,
        content: receptionistIntro(title),
      },
    ]);
  }, [open, thread.length, title]);

  // Keep the newest message in view.
  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [thread, open]);

  // Escape closes the panel (it's a popover, not a focus-trapping modal).
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open]);

  const submitLead = useCallback(
    async (
      lead: { firstName?: string; lastName?: string; email?: string; phone?: string; summary?: string },
      transcript: ChatMessage[],
    ) => {
      if (leadSubmittedRef.current || !slug) return;
      leadSubmittedRef.current = true;
      // Message = the model's summary of what the visitor wants, with a short
      // transcript tail for context in the note/notification/follow-up task.
      const tail = transcript
        .slice(-8)
        .map((m) => `${m.role === "user" ? "Visitor" : "Receptionist"}: ${m.content}`)
        .join("\n");
      const message = [lead.summary?.trim(), tail ? `Chat transcript:\n${tail}` : ""]
        .filter(Boolean)
        .join("\n\n");
      try {
        const res = await fetch("/api/ic/submit", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            slug,
            formSource: "receptionist",
            ...(conversationIdRef.current ? { conversationId: conversationIdRef.current } : {}),
            firstName: lead.firstName ?? "",
            lastName: lead.lastName ?? "",
            email: lead.email ?? "",
            phone: lead.phone ?? "",
            message,
            selectedCtaPath:
              waitlistContextRef.current?.sessionPurpose === "consultation"
                ? "schedule"
                : "message",
          }),
        });
        if (!res.ok) throw new Error(`submit ${res.status}`);
        const result = await res.json();
        trackEvent("receptionist_lead_handoff_completed", { flow: "general" });
        if (redirectAfterSubmission(result.confirmation)) return;
        setThread((t) => [
          ...t,
          { role: "system", content: result.confirmation?.message || "Thank you!  If a reply is needed, we'll be in touch soon." },
        ]);
      } catch (e) {
        console.error("[receptionist] lead submit failed:", e);
        // Allow a retry on a later turn if the model re-emits the lead.
        leadSubmittedRef.current = false;
      }
    },
    [slug],
  );

  const onBookingConversationConfirmed = useCallback((signedContext: SignedReceptionistContext) => {
    if (!slug || signedContext.slug !== slug || !signedContext.historyToken) return;
    const previousTranscript = transcriptRef.current;
    const hasPreviousPrefix = previousTranscript.length <= signedContext.transcript.length
      && previousTranscript.every((message, index) =>
        message.role === signedContext.transcript[index]?.role
        && message.content === signedContext.transcript[index]?.content,
      );
    const appended = hasPreviousPrefix
      ? signedContext.transcript.slice(previousTranscript.length)
      : [];
    conversationIdRef.current = signedContext.conversationId;
    historyTokenRef.current = signedContext.historyToken;
    transcriptRef.current = signedContext.transcript;
    saveReceptionistBookingContext(signedContext);
    const assistantRows = appended
      .filter((message) => message.role === "assistant")
      .map((message) => ({ role: "assistant" as const, content: message.content }));
    if (assistantRows.length) setThread((current) => [...current, ...assistantRows]);
  }, [slug]);

  // POST one turn to the chat endpoint using the current token/nonce refs.
  const postChatTurn = useCallback(
    async (messages: ChatMessage[]) => {
      // A booking completed from ClassSchedule can return a newer signed
      // server transcript. Adopt it only when it extends this same conversation;
      // preserve the visitor turn already assembled by the caller.
      const handoff = slug ? getReceptionistBookingContext(slug) : null;
      if (handoff) {
        const previousTranscript = transcriptRef.current;
        const isSameConversationPrefix = previousTranscript.length <= handoff.transcript.length
          && previousTranscript.every((message, index) =>
            message.role === handoff.transcript[index]?.role
            && message.content === handoff.transcript[index]?.content,
          );
        if (isSameConversationPrefix && (
          handoff.historyToken !== historyTokenRef.current
          || handoff.transcript.length !== previousTranscript.length
        )) {
          const pendingMessages = messages.slice(previousTranscript.length);
          messages.splice(0, messages.length, ...handoff.transcript, ...pendingMessages);
          historyTokenRef.current = handoff.historyToken;
          conversationIdRef.current = handoff.conversationId;
          transcriptRef.current = handoff.transcript;
        }
      }
      const res = await fetch("/api/ic/receptionist/chat", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          slug,
          messages,
          // Integrity token + conversation nonce from the previous reply —
          // proves the assistant turns in `messages` are exactly what the
          // server said, for this conversation.
          ...(historyTokenRef.current ? { historyToken: historyTokenRef.current } : {}),
          ...(conversationIdRef.current ? { conversationId: conversationIdRef.current } : {}),
          ...(waitlistContextRef.current ? { waitlist: waitlistContextRef.current } : {}),
        }),
      });
      const data = await res.json().catch(() => null);
      return { res, data };
    },
    [slug],
  );

  // Guided flow finish: post the lead, then ask the server ONE composed
  // pricing question. Only the reply is shown; the guided Q&A rows stay
  // local. On any chat failure the fallback is honest — the visitor's
  // details ARE in (the lead posted first), so we say the team will follow
  // up with pricing rather than inventing numbers.
  const finishGuidedFlow = useCallback(async () => {
    const a = guidedAnswersRef.current;
    setGuidedStage(null);
    if (isPreview) {
      setThread((t) => [
        ...t,
        {
          role: "assistant",
          local: true,
          content: "This is a design preview — on your live website I'll pass these details along and share your saved pricing.",
        },
      ]);
      return;
    }
    setSending(true);
    try {
      // 1. Lead first — the visitor's details must land even if the chat
      //    answer fails. The guided name was checked before advancing; do not
      //    split the original free-text reply into contact fields here.
      //    The message is a fixed marker — they asked for pricing; the old
      //    "what do you need help with?" question was cut (it read as a
      //    non-sequitur right after the visitor already said they want
      //    pricing).
      // Mint and persist the canonical conversation before lead capture so
      // the submission can associate this exact transcript with the contact.
      // A chat failure never prevents the contact submission below.
      const composed = `My name is ${a.name}. ${a.context ? `${a.context} ` : ""}Can you tell me how your pricing works?`;
      const messages: ChatMessage[] = [
        ...transcriptRef.current,
        { role: "user", content: composed.slice(0, MAX_MESSAGE_CHARS) },
      ];
      let pricingReply = "";
      try {
        const { res, data } = await postChatTurn(messages);
        if (res.ok && data?.reply) {
          rememberBookingContext(data, [...messages, { role: "assistant", content: data.reply }]);
          pricingReply = data.reply;
        }
      } catch (e) {
        console.error("[receptionist] guided pricing answer failed:", e);
      }
      try {
        const res = await fetch("/api/ic/submit", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            slug,
            formSource: "receptionist",
            ...(conversationIdRef.current ? { conversationId: conversationIdRef.current } : {}),
            firstName: a.firstName,
            lastName: a.lastName,
            email: a.email,
            phone: a.phone,
            message: a.context
              ? `Asked for pricing via the receptionist. Visitor also said: ${a.context}`
              : "Asked for pricing via the receptionist.",
            selectedCtaPath: "pricing",
          }),
        });
        if (!res.ok) throw new Error(`submit ${res.status}`);
        const result = await res.json();
        leadSubmittedRef.current = true;
        trackEvent("receptionist_lead_handoff_completed", { flow: "pricing" });
        if (window.location.pathname === "/") {
          trackEvent("sales_pricing_lead_submitted", { flow: "pricing" });
        }
        if (redirectAfterSubmission(result.confirmation)) return;
        setThread((t) => [
          ...t,
          { role: "system", content: result.confirmation?.message || "Thank you!  If a reply is needed, we'll be in touch soon." },
        ]);
      } catch (e) {
        console.error("[receptionist] guided lead submit failed:", e);
      }

      if (!pricingReply) {
        setThread((t) => [
          ...t,
          {
            role: "assistant",
            local: true,
            content: "Thanks! I can't pull up our pricing right this moment, but the team has your details and will follow up with pricing soon.",
          },
        ]);
        return;
      }
      setThread((t) => [...t, { role: "assistant", content: pricingReply }]);
    } catch (e) {
      console.error("[receptionist] guided pricing turn failed:", e);
      setThread((t) => [
        ...t,
        {
          role: "assistant",
          local: true,
          content: "Thanks! I can't pull up our pricing right this moment, but the team has your details and will follow up with pricing soon.",
        },
      ]);
    } finally {
      setSending(false);
    }
  }, [isPreview, slug, postChatTurn, rememberBookingContext]);

  // Handle one guided answer: store it, then either ask the next question
  // (local assistant row) or finish. Light email check — anything without an
  // "@" gets a friendly re-ask instead of a dead-end.
  const handleGuidedAnswer = useCallback(
    (stage: GuidedStage, text: string) => {
      const a = guidedAnswersRef.current;
      if (stage === "name") {
        const name = parseVisitorNameAnswer(text);
        if (!name) {
          // Keep the visitor's actual question for the grounded pricing reply
          // and contact note; don't turn "Hi, I'm looking for..." into a name.
          a.context = [a.context, text].filter(Boolean).join(" ").slice(0, 600);
          const asksAboutBusiness = /[?]|\b(?:looking|interested|want|need|how|what|when|where|do you|can you)\b/i.test(text);
          if (asksAboutBusiness && !isPreview) {
            void (async () => {
              setSending(true);
              try {
                let messages: ChatMessage[] = [...transcriptRef.current, { role: "user", content: text }];
                let { res, data } = await postChatTurn(messages);
                if (!res.ok && data?.code === "history_token_invalid") {
                  historyTokenRef.current = null;
                  conversationIdRef.current = null;
                  transcriptRef.current = [];
                  if (slug) clearReceptionistBookingContext(slug);
                  messages = [{ role: "user", content: text }];
                  ({ res, data } = await postChatTurn(messages));
                }
                if (!res.ok || !data?.reply) {
                  setThread((t) => [
                    ...t,
                    { role: "assistant", local: true, content: "I can't pull that up just yet. What's your name so the team can help?" },
                  ]);
                  return;
                }
                rememberBookingContext(data, [...messages, { role: "assistant", content: data.reply }]);
                if (data.requestsPhone === true) setPhoneConsentOffered(true);
                const alreadyAskedName = /\b(?:your name|who am i speaking with|who should (?:we|i) ask for)\b/i.test(data.reply);
                setThread((t) => [
                  ...t,
                  { role: "assistant", content: data.reply },
                  ...(!alreadyAskedName
                    ? [{ role: "assistant" as const, local: true, content: "And what's your name?" }]
                    : []),
                ]);
              } catch (error) {
                console.error("[receptionist] guided question failed:", error);
                setThread((t) => [
                  ...t,
                  { role: "assistant", local: true, content: "I can't pull that up just yet. What's your name so the team can help?" },
                ]);
              } finally {
                setSending(false);
              }
            })();
            return;
          }
          setThread((t) => [
            ...t,
            { role: "assistant", local: true, content: "I can help with that. What's your name?" },
          ]);
          return;
        }
        a.name = name.fullName;
        a.firstName = name.firstName;
        a.lastName = name.lastName;
        if (requireLastName && !name.lastName) {
          setGuidedStage("lastName");
          setThread((t) => [
            ...t,
            { role: "assistant", local: true, content: `Thanks, ${name.firstName}. ${GUIDED_PROMPTS.lastName}` },
          ]);
          return;
        }
        setGuidedStage("email");
        setThread((t) => [...t, { role: "assistant", local: true, content: GUIDED_PROMPTS.email }]);
        return;
      }
      if (stage === "lastName") {
        const response = text.replace(/^(?:my last name is|last name is|it's)\s+/i, "");
        const surname = parseVisitorNameAnswer(response);
        if (!surname) {
          setThread((t) => [
            ...t,
            { role: "assistant", local: true, content: "Sorry, what was your last name?" },
          ]);
          return;
        }
        a.lastName = surname.firstName.toLocaleLowerCase() === a.firstName.toLocaleLowerCase() && surname.lastName
          ? surname.lastName
          : surname.fullName;
        a.name = `${a.firstName} ${a.lastName}`;
        setGuidedStage("email");
        setThread((t) => [...t, { role: "assistant", local: true, content: GUIDED_PROMPTS.email }]);
        return;
      }
      if (stage === "email") {
        if (!text.includes("@")) {
          setThread((t) => [
            ...t,
            { role: "assistant", local: true, content: "Hmm, that doesn't look like an email address. Mind checking it?" },
          ]);
          return;
        }
        a.email = text;
        if (askPhone) {
          setGuidedStage("phone");
          setPhoneConsentOffered(true);
          setThread((t) => [...t, { role: "assistant", local: true, content: GUIDED_PROMPTS.phone }]);
          return;
        }
        void finishGuidedFlow();
        return;
      }
      if (stage === "phone") {
        a.phone = text;
        setGuidedStage(null);
        setPhoneConsentOffered(false);
        void finishGuidedFlow();
        return;
      }
    },
    [askPhone, requireLastName, isPreview, slug, postChatTurn, finishGuidedFlow, rememberBookingContext],
  );

  const continueAfterConsent = useCallback(() => {
    const pending = pendingConsent;
    if (!pending) return;
    setPendingConsent(null);
    if (pending.kind === "guided") {
      void finishGuidedFlow();
      return;
    }
    void submitLead(pending.lead, pending.transcript);
  }, [finishGuidedFlow, pendingConsent, submitLead]);

  const send = useCallback(async () => {
    const text = input.trim().slice(0, MAX_MESSAGE_CHARS);
    if (!text || sending) return;
    if (!firstMessageTrackedRef.current) {
      firstMessageTrackedRef.current = true;
      trackEvent("receptionist_first_message_sent", {
        flow: guidedStage ? "pricing" : "general",
      });
    }
    setInput("");
    setThread((t) => [...t, { role: "user", content: text }]);
    // Mid-guided-flow answers are handled locally (never sent to the model).
    if (guidedStage) {
      handleGuidedAnswer(guidedStage, text);
      return;
    }
    if (isPreview) {
      // Design preview never POSTs — show a friendly notice instead.
      setThread((t) => [
        ...t,
        {
          role: "assistant",
          local: true,
          content: "This is a design preview — on your live website I'll answer from your approved knowledgebase.",
        },
      ]);
      return;
    }
    setSending(true);
    try {
      // transcriptRef holds exactly the turns the server signed with
      // historyToken (never the greeting/system/local rows).
      let messages: ChatMessage[] = [...transcriptRef.current, { role: "user", content: text }];
      let { res, data } = await postChatTurn(messages);
      if (!res.ok && data?.code === "history_token_invalid") {
        // The server rejected our history token — almost always because the
        // per-process signing secret rotated (server restart/redeploy) mid-
        // conversation. Recover gracefully: drop the signed history and retry
        // as a fresh conversation carrying only the visitor's latest message.
        setThread((t) => [
          ...t,
          { role: "system", content: "One moment — picking the conversation back up…" },
        ]);
        historyTokenRef.current = null;
        conversationIdRef.current = null;
        transcriptRef.current = [];
        if (slug) clearReceptionistBookingContext(slug);
        messages = [{ role: "user", content: text }];
        ({ res, data } = await postChatTurn(messages));
      }
      if (!res.ok || !data?.reply) {
        const line =
          (data && typeof data.error === "string" && data.error) ||
          "The receptionist can't chat right now. Please try again in a moment.";
        setThread((t) => [...t, { role: "system", content: line }]);
        return;
      }
      const withReply: ChatMessage[] = [...messages, { role: "assistant", content: data.reply }];
      // Store the integrity token + conversation nonce for the next turn.
      rememberBookingContext(data, withReply);
      setThread((t) => [...t, { role: "assistant", content: data.reply }]);
      if (data.requestsPhone === true && !data.lead?.phone) {
        setPhoneConsentOffered(true);
      }
      if (data.lead && (data.lead.email || data.lead.phone)) {
        if (data.lead.phone?.trim()) {
          if (phoneConsentOffered) {
            setPhoneConsentOffered(false);
            void submitLead(data.lead, withReply);
          } else {
            setPendingConsent({ kind: "general", lead: data.lead, transcript: withReply });
          }
        } else {
          void submitLead(data.lead, withReply);
        }
      }
    } catch (e) {
      console.error("[receptionist] chat failed:", e);
      setThread((t) => [
        ...t,
        { role: "system", content: "The receptionist can't chat right now. Please try again in a moment." },
      ]);
    } finally {
      setSending(false);
    }
  }, [input, sending, slug, isPreview, submitLead, postChatTurn, guidedStage, handleGuidedAnswer, phoneConsentOffered, rememberBookingContext]);

  if (!mounted) return null;
  // A visitor must still reach their booking when the remaining availability
  // becomes empty after their reservation or after reopening the conversation.
  const hasBookingConfirmation = !!savedConfirmation(slug, conversationIdRef.current);

  return createPortal(
    <div className="vault-template-root" style={themeVars}>
      {/* Launcher — hidden while the panel is open (the panel has its own X). */}
      {!open && (
        <button
          type="button"
          onClick={openFromLauncher}
          aria-label={`Ask ${title} for help`}
          className={`vt-accent-outline fixed bottom-5 right-5 z-[90] flex h-14 items-center rounded-2xl transition-[transform,width] hover:scale-[1.02] motion-reduce:transition-none motion-reduce:hover:scale-100 ${
            launcherExpanded && !embedded
              ? "w-[76px] gap-1.5 px-2 sm:w-auto sm:max-w-[320px] sm:gap-3 sm:px-2.5 sm:pr-4"
              : "w-14 justify-center"
          }`}
          style={{
            background: "var(--theme-cta-bg, #1F1F1F)",
            color: "var(--theme-cta-fg, #FFFFFF)",
          }}
          data-testid="button-receptionist-launcher"
        >
          <span
            className="relative flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-full"
            style={{
              backgroundColor: "color-mix(in srgb, var(--theme-accent-dark) 14%, transparent)",
              color: "var(--theme-accent-dark)",
            }}
            aria-hidden="true"
          >
            {launcherImageUrl && !launcherImageFailed
              ? <img src={launcherImageUrl} alt="" data-testid="img-receptionist-launcher" className="absolute inset-0 h-full w-full object-cover" style={launcherImageFramingStyle(launcherImageZoom, launcherImageFocalX, launcherImageFocalY)} onError={() => setLauncherImageFailed(true)} />
              : <UserRound className="h-5 w-5" data-testid="icon-receptionist-launcher-fallback" />}
          </span>
          {launcherExpanded && !embedded && (
            <>
              <span className="text-[12px] font-semibold sm:hidden">Ask</span>
              <span className="hidden min-w-0 text-left leading-tight sm:block">
                <span className="block truncate text-[14px] font-semibold" style={{ color: "var(--theme-cta-fg, #FFFFFF)" }}>
                  {launcherText}
                </span>
                <span className="mt-0.5 flex items-center gap-1 text-[12px] font-medium">
                  {launcherSubtext} <ArrowRight className="h-3 w-3" aria-hidden="true" />
                </span>
              </span>
            </>
          )}
        </button>
      )}

      {open && (
        <div
          role="dialog"
          aria-label={title}
          className="vt-accent-outline fixed z-[95] flex flex-col overflow-hidden inset-x-0 bottom-0 h-[80dvh] rounded-t-[20px] sm:inset-x-auto sm:right-5 sm:bottom-5 sm:h-[560px] sm:max-h-[calc(100dvh-40px)] sm:w-[380px] sm:rounded-[20px]"
          style={{ backgroundColor: "#FFFFFF", color: "var(--theme-recept-body)" }}
          data-testid="panel-receptionist"
        >
          {/* Header */}
          <div
            className="flex items-center justify-between px-5 py-4"
            style={{ backgroundColor: "#FFFFFF", borderBottom: "1px solid var(--theme-recept-border)" }}
          >
            <div className="flex items-center gap-3">
              <span
                className="flex h-10 w-10 items-center justify-center rounded-xl shadow-sm"
                style={{ backgroundColor: "var(--theme-accent)", color: "#FFFFFF" }}
              >
                <MessageSquare className="h-5 w-5" />
              </span>
              <div className="leading-tight">
                <div className="text-[16px] font-semibold" style={{ color: "var(--theme-recept-title)" }} data-testid="text-receptionist-name">
                  {title}
                </div>
                <div className="text-[13px]" style={{ color: "var(--theme-recept-muted)" }}>
                  Digital Receptionist
                </div>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setOpen(false)}
              aria-label="Close chat"
              className="rounded-lg p-2 transition-colors hover:bg-[#f5f4f1]"
              style={{ color: "var(--theme-recept-muted)" }}
              data-testid="button-receptionist-close"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Thread */}
          <div ref={scrollRef} className="flex-1 space-y-4 overflow-y-auto px-5 py-5" style={{ backgroundColor: "var(--theme-recept-thread-bg)" }} data-testid="list-receptionist-thread">
            {thread.map((m, i) =>
              m.role === "system" ? (
                <div
                  key={i}
                  className="mx-auto max-w-[90%] text-center text-[12px]"
                  style={{ color: "var(--theme-recept-muted)" }}
                  data-testid={`text-receptionist-system-${i}`}
                >
                  {m.content}
                </div>
              ) : (
                <div key={i} className={m.role === "user" ? "flex justify-end" : "flex justify-start"}>
                  <div
                    className="max-w-[85%] whitespace-pre-wrap px-4 py-2.5 text-[14.5px] leading-relaxed shadow-sm"
                    style={
                      m.role === "user"
                        ? { backgroundColor: "var(--theme-accent)", color: "#FFFFFF", borderRadius: "14px 14px 4px 14px" }
                        : { backgroundColor: "#FFFFFF", color: "var(--theme-recept-body)", borderRadius: "14px 14px 14px 4px", border: "1px solid var(--theme-recept-border)" }
                    }
                    data-testid={`text-receptionist-msg-${i}`}
                  >
                    {m.role === "assistant"
                      ? renderWithLinks(
                          m.local && (m.greeting || (i === 0 && m.content === LEGACY_DIGI_INTRO))
                            ? receptionistIntro(title)
                            : m.content,
                          navigateToSessions,
                          legalHomeHref,
                          navigateToSlide,
                        )
                      : m.content}
                  </div>
                </div>
              ),
            )}
            {(bookingAvailable || hasBookingConfirmation || bookingOpen) && (
              <div className="space-y-3" data-testid="receptionist-booking-controls">
                {(bookingAvailable || hasBookingConfirmation) && !bookingOpen && (
                  <button
                    type="button"
                    onClick={() => setBookingOpen(true)}
                    className="rounded-lg px-4 py-2.5 text-[13px] font-semibold"
                    style={{ background: "var(--theme-accent)", color: "#fff" }}
                    data-testid="button-open-booking"
                  >
                    {hasBookingConfirmation ? "Manage Your Appointment" : "Book an Appointment"}
                  </button>
                )}
                {bookingOpen && (
                  <AppointmentBooking
                    slug={slug}
                    isPreview={isPreview}
                    initialContact={bookingContact}
                    context={historyTokenRef.current ? {
                      slug: slug ?? "",
                      conversationId: conversationIdRef.current,
                      historyToken: historyTokenRef.current,
                      transcript: transcriptRef.current,
                    } : null}
                    onClose={() => setBookingOpen(false)}
                    onConversationConfirmed={onBookingConversationConfirmed}
                  />
                )}
              </div>
            )}
            {sending && (
              <div className="flex justify-start">
                <div
                  className="px-4 py-2.5 shadow-sm"
                  style={{ backgroundColor: "#FFFFFF", border: "1px solid var(--theme-recept-border)", borderRadius: "14px 14px 14px 4px" }}
                  data-testid="status-receptionist-typing"
                >
                  <Loader2 className="h-4 w-4 animate-spin" style={{ color: "var(--theme-recept-muted)" }} />
                </div>
              </div>
            )}
          </div>

          {/* Composer / explicit SMS consent gate */}
          <div className="p-4" style={{ backgroundColor: "#FFFFFF", borderTop: "1px solid var(--theme-recept-border)" }}>
            <div className="space-y-3">
                <div
                  className="flex items-end gap-3 rounded-xl p-1.5 transition-all focus-within:ring-1"
                  style={{
                    backgroundColor: "var(--theme-recept-thread-bg)",
                    border: "1px solid var(--theme-recept-border-strong)",
                    ["--tw-ring-color" as string]: "var(--theme-accent)",
                  }}
                >
                <textarea
                  ref={inputRef}
                  name="receptionistMessage"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      void send();
                    }
                  }}
                  rows={1}
                  maxLength={MAX_MESSAGE_CHARS}
                  placeholder="Type a message…"
                  className="max-h-28 min-h-[36px] flex-1 resize-none bg-transparent px-3 py-2 text-[14px] outline-none placeholder:text-[#a09d96]"
                  style={{ color: "var(--theme-recept-title)" }}
                  data-testid="input-receptionist-message"
                />
                <button
                  type="button"
                  onClick={() => void send()}
                  disabled={sending || !input.trim()}
                  aria-label="Send message"
                  className="mb-1 mr-1 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg shadow-sm transition-transform hover:scale-105 disabled:opacity-40 disabled:hover:scale-100"
                  style={{ backgroundColor: "var(--theme-accent)", color: "#FFFFFF" }}
                  data-testid="button-receptionist-send"
                >
                  <Send className="h-4 w-4" />
                </button>
                </div>
            </div>
          </div>
        </div>
      )}
    </div>,
    document.body,
  );
}
